import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';

import Modal from '../../../components/Modal';

import { IFormBusca } from '../index';
import ClinicasCadastroPagina, { IClinica } from '../ClinicasCadastro';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

interface IClinicas
  extends Omit<
    IClinica,
    | 'CliJuros'
    | 'CliMulta'
    | 'CliDiasVencimento'
    | 'CliDiasCobrarJuros'
    | 'CliLembreteVencimento'
    | 'CliLembreteVencido5'
    | 'CliLembreteVencido10'
    | 'CliIdentificaPedidoExame'
    | 'CliFuncionaSabado'
    | 'CliAtendeConvenio'
    | 'CliExibeConvenioMedico'
    | 'CliPerfilAlteraNomeAssociado'
    | 'CliPerfilAcessaProcedimentos'
    | 'CliPerfilCancelaCartao'
    | 'CliPerfilValorCartao'
    | 'CliUsaPainel'
    | 'CliCobrancaAutomatica'
    | 'CliIdentificaPedidoExame'
    | 'CliFuncionaSabado'
    | 'CliAtendeConvenio'
    | 'CliExibeConvenioMedico'
    | 'CliUsaPainel'
    | 'CliCobrancaAutomatica'
  > {
  ativoFormatado?: string;
}

export interface IRetornoListagemAgenda {
  clinicas: IClinicas[];
}
export interface RefObject {
  addClinica(
    dadosClinica: IClinica,
    cliCodigo: number | undefined,
  ): Promise<void>;
}
type Props = {
  dados?: IFormBusca;
  ref: Ref<RefObject>;
};

const ListaAgendas: React.FC<Props> = forwardRef(
  (prop: Props, ref: Ref<RefObject>) => {
    const [showModalCadastro, setShowModalCadastro] = useState(false);
    const { addToast } = useToast();
    const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(
      undefined,
    );
    const [loading, setLoading] = useState(false);

    const [listagemClinicas, setListagemClinicas] = useState<IClinicas[]>([]);

    const [clinicaSelecionada, setClinicaSelecionada] = useState<
      number | undefined
    >(undefined);

    useEffect(() => {
      setPaginacao({
        termoBusca: prop.dados || {
          situacao: 'true',
        },
        pagina: 1,
        limite: 20,
      });
    }, [prop.dados]);

    useEffect(() => {
      async function buscaClinicas(): Promise<void> {
        if (paginacao) {
          try {
            setLoading(true);
            const { situacao } = paginacao.termoBusca;
            let query = '';

            if (situacao !== '') query = `${query}&CliAtivo=${situacao}`;

            const response = await api.get<IClinicas[]>(
              `/clinicas?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
            );

            const listagemClinicasApi: IClinicas[] = response.data;

            const listagemClinicasApiFormatada = listagemClinicasApi.map(
              lista => {
                return {
                  ...lista,
                  ativoFormatado: lista.CliAtivo ? 'Sim' : 'Não',
                };
              },
            );

            setListagemClinicas(listagemClinicasApiFormatada);
          } catch (error) {
            const mensagemErro = getResponseErrors(error);
            addToast({
              title: 'Não foi possível listar agendas',
              type: 'error',
              description: mensagemErro,
            });
          } finally {
            setLoading(false);
          }
        }
      }
      if (paginacao) buscaClinicas();
    }, [paginacao, addToast]);

    const addClinica = useCallback(
      async (dadosClinica: IClinica, ConCodigo: number | undefined) => {
        let response;
        setLoading(true);
        try {
          if (!ConCodigo) {
            addToast({
              title: 'Edição de dados!',
              type: 'error',
              description: 'Nenhuma clínica selecionada.',
              timeAlive: 6000,
            });
          }
          response = await api.put<IClinica>(
            `/clinicas/${ConCodigo}`,
            dadosClinica,
          );

          const dadosRetorno = response.data;

          const retornoClinica: IClinicas = {
            ...dadosRetorno,
            ativoFormatado: dadosRetorno.CliAtivo ? 'Sim' : 'Não',
          };

          setListagemClinicas(state => {
            return state
              ? [retornoClinica].concat(
                  state.filter(
                    cli => cli.CliCodigo !== retornoClinica.CliCodigo,
                  ),
                )
              : [retornoClinica];
          });
          setShowModalCadastro(false);
          addToast({ title: 'Clínica salva', type: 'success' });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Não foi possível salvar clínica',
            type: 'error',
            description: mensagemErro,
            timeAlive: 10000,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    useImperativeHandle(ref, () => ({ addClinica }));

    const handleEditar = useCallback((cliCodigo: number) => {
      setClinicaSelecionada(cliCodigo);
      setShowModalCadastro(true);
    }, []);

    return (
      <>
        {showModalCadastro && (
          <Modal
            titulo="Cadastro de Clínica"
            handleFecharModal={() => setShowModalCadastro(false)}
            large
            show={showModalCadastro}
          >
            <ClinicasCadastroPagina
              cliCodigo={clinicaSelecionada}
              addClinica={addClinica}
            />
          </Modal>
        )}
        {loading && <Loading />}

        {listagemClinicas ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Nome</th>
                  <th scope="col">CNPJ</th>
                  <th scope="col">Telefone</th>
                  <th scope="col">E-mail</th>
                  <th scope="col">Cód. RSW</th>
                  <th scope="col">Bloqueio Pag</th>
                  <th scope="col">Ativo</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {listagemClinicas.map((lista: IClinicas) => (
                  <tr
                    key={lista.CliCodigo}
                    style={{ fontSize: 'Small', height: '30px' }}
                    className={`${!lista.CliAtivo ? 'text-muted' : ''}`}
                  >
                    <td>{lista.CliNome}</td>
                    <td>{lista.CliCNPJ}</td>
                    <td>{lista.CliTelefone}</td>
                    <td style={{ width: '70px' }}>{lista.CliEmail}</td>
                    <td>{lista.CliCodigoRioSulWeb}</td>
                    <td style={{ width: '70px' }}>
                      {lista.CliBloqueioPagamento}
                    </td>
                    <td style={{ width: '60px' }}>{lista.ativoFormatado}</td>
                    <td className="p-0" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="primary-outline"
                        className="py-0"
                        title={`${lista.CliCodigo}`}
                        onClick={
                          () => handleEditar(lista.CliCodigo)
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>Listagem clinicas</h5>
          </div>
        )}
      </>
    );
  },
);

export default ListaAgendas;
